import {
  Button, Flex, Text
} from '@huspy/briks-web';
import { BaseSlido } from '@modules/core/components/BaseSlido';
import { BankApplication } from '@modules/opportunities/entities/bankApplication/bankApplication.entity';
import { useTranslation } from 'react-i18next';

import BankIcon from '@components/v2/BankIcon';
import { useState } from 'react';
import { useForm, zodResolver } from '@mantine/form';
import { CaseCreationSubmitApplicationsRoute } from '@modules/opportunities/presentation/routes';
import { useGlobalStore } from '@modules/core/store';
import useSubmitToBank from '@modules/opportunities/entities/bankApplication/mutation/useSubmitToBank';
import { Opportunity } from '@modules/opportunities/entities/opportunity/opportunity.entity';
import useSetEmailPreferences from '@modules/clients/entities/client/mutation/useSetEmailPreferences';
import { SuccessMessage } from '../SuccessMessage';
import { EmailSourceSelection } from './EmailSourceSelection';
import {
  ComposeEmailFormValues,
  EMAIL_CHANNEL_PROVIDER,
  EmailChannelProviderType,
  MAIL_ACTION_TYPES,
  MailActionTypes,
} from './const';
import { ComposeEmail } from './ComposeEmail';
import { emailSubmissionSchema } from './validator';
import { bodyDefaultValue, subjectDefaultValue } from './emailTemplateHelpers';

const baseTranslation = 'opportunity.caseSubmission.submitApplications.bankAndHuspySubmission.directToBankSlido';

export const DirectToBankSubmissionSlido = ({
  opportunity,
  bankApplication,
  opened,
  close,
  emailChannelProviderDefaultValue,
  isThirdPartyProvider,
}: {
  opportunity: Opportunity;
  bankApplication: BankApplication;
  opened: boolean;
  close: () => void;
  emailChannelProviderDefaultValue: EmailChannelProviderType;
  isThirdPartyProvider: boolean;
}) => {
  const { t } = useTranslation();
  const { opportunityId } = CaseCreationSubmitApplicationsRoute.useParams();
  const { mutateAsync: setEmailPreference, isPending: isSetEmailPreferenceLoading } = useSetEmailPreferences(
    opportunity.referrer_external_id
  );
  const { user } = useGlobalStore();
  const { mutateAsync, isPending } = useSubmitToBank(
    opportunityId,
    bankApplication.bank_application_external_id,
    opportunity.bank_applications
  );

  const form = useForm<ComposeEmailFormValues>({
    initialValues: {
      sendTo: [],
      subject: subjectDefaultValue(opportunity, bankApplication),
      cc: [],
      body: bodyDefaultValue(opportunity, bankApplication),
    },
    validateInputOnChange: true,
    validate: zodResolver(emailSubmissionSchema),
  });

  const [mailActionType, setMailActionType] = useState<MailActionTypes>(
    MAIL_ACTION_TYPES.SELECT_EMAIL_CHANNEL
  );
  const [emailChannelProvider, setEmailChannelProvider] = useState<EmailChannelProviderType>(
    emailChannelProviderDefaultValue
  );

  const reset = () => {
    setMailActionType(MAIL_ACTION_TYPES.SELECT_EMAIL_CHANNEL);
    setEmailChannelProvider(emailChannelProviderDefaultValue);
    close();
  };

  const handleContinueClick = async () => {
    await setEmailPreference(emailChannelProvider);
    setMailActionType(MAIL_ACTION_TYPES.COMPOSE_EMAIL);
    if (emailChannelProvider === EMAIL_CHANNEL_PROVIDER.SELF) {
      form.setFieldValue('sendTo', [user?.email!]);
    } else if (emailChannelProvider === EMAIL_CHANNEL_PROVIDER.THIRD_PARTY) {
      form.setFieldValue('sendTo', []);
      form.reset();
    }
  };

  const handleSubmit = async () => {
    form.validate();
    if (form.isValid()) {
      const data = form.values;
      await mutateAsync({
        to_email_addresses:
          emailChannelProvider === EMAIL_CHANNEL_PROVIDER.SELF
            ? null
            : data.sendTo,
        cc_addresses: data.cc,
        bcc_addresses: [],
        email_subject: data.subject,
        email_body: data.body,
        provider: emailChannelProvider,
      });
      setMailActionType(MAIL_ACTION_TYPES.SUCCESS);
    }
  };

  const buttonToRender = () => {
    if (mailActionType === MAIL_ACTION_TYPES.SELECT_EMAIL_CHANNEL) {
      return (
        <Button
          size='md'
          fontSize='md'
          onClick={ handleContinueClick }
          loading={ isSetEmailPreferenceLoading }
          variant={ isSetEmailPreferenceLoading ? 'outline' : 'primary' }
          disabled={ emailChannelProvider === EMAIL_CHANNEL_PROVIDER.THIRD_PARTY
            && !isThirdPartyProvider }
          data-test='continue-email-submission-btn'
        >
          {t(`${baseTranslation}.continueEmailSubmissionButton`)}
        </Button>
      );
    }

    if (mailActionType === MAIL_ACTION_TYPES.COMPOSE_EMAIL) {
      return (
        <Button
          size='md'
          fontSize='md'
          onClick={ handleSubmit }
          loading={ isPending }
          variant={ isPending ? 'outline' : 'primary' }
          data-test='send-btn'
        >
          {t(`${baseTranslation}.sendButton`)}
        </Button>
      );
    }

    if (mailActionType === MAIL_ACTION_TYPES.SUCCESS) {
      return (
        <Button
          size='md'
          fontSize='md'
          onClick={ reset }
          data-test='discard-btn'
        >
          {t(`${baseTranslation}.doneButton`)}
        </Button>
      );
    }
  };

  const bodyToRender = () => {
    if (mailActionType === MAIL_ACTION_TYPES.SELECT_EMAIL_CHANNEL) {
      return (
        <EmailSourceSelection
          bankApplication={ bankApplication }
          accountExternalId={ opportunity.referrer_external_id }
          emailChannelProvider={ emailChannelProvider }
          setEmailChannelProvider={ (_emailChannel) =>
            setEmailChannelProvider(_emailChannel) }
        />
      );
    }

    const bankDocuments = opportunity.bank_applications.flatMap((bankApp) =>
      bankApp.documents.map((doc) => ({
        type: 'bank' as 'bank',
        name: doc.file_name,
        id: doc.document_external_id,
      }))) ?? [];

    const personalDocuments = opportunity.applicants.flatMap(
      (applicant) =>
        applicant.documents?.map((doc) => ({
          type: 'personal' as 'personal',
          name: doc.file_name,
          id: doc.document_external_id,
          applicantId: applicant.opportunity_applicant_external_id!,
        }))!
    ) ?? [];

    if (mailActionType === MAIL_ACTION_TYPES.COMPOSE_EMAIL) {
      return (
        <ComposeEmail
          attachedFiles={ [...bankDocuments, ...personalDocuments] }
          form={ form }
          emailChannelProvider={ emailChannelProvider }
          goBack={ () =>
            setMailActionType(MAIL_ACTION_TYPES.SELECT_EMAIL_CHANNEL) }
        />
      );
    }
    if (mailActionType === MAIL_ACTION_TYPES.SUCCESS) {
      return <SuccessMessage isSubmittedToBank />;
    }
  };

  return (
    <BaseSlido
      title={ (
        <Flex gap='2' align='center' justify='center' p='8'>
          <BankIcon
            icon={ bankApplication.bank_details.icon }
            height='48px'
            width='48px'
            pos='relative'
          />
          <Text size='3xl' weight='medium'>
            {t(`${baseTranslation}.header`, { bankName: bankApplication.bank_details.name })}
          </Text>
        </Flex>
      ) }
      footer={ (
        <Flex w='100%' justify='end' align='end'>
          {buttonToRender()}
        </Flex>
      ) }
      opened={ opened }
      close={ reset }
    >
      {bodyToRender()}
    </BaseSlido>
  );
};
